import {
  Cloud,
  Sun,
  CloudRain,
  CloudSnow,
  Wind,
  Droplets,
  RefreshCw,
  MapPin,
  AlertTriangle,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

interface WeatherData {
  location: string;
  temperature: number;
  feelsLike: number;
  condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'windy';
  description: string;
  humidity: number;
  windSpeed: number;
  forecast: Array<{
    day: string;
    high: number;
    low: number;
    condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'windy';
  }>;
}

const WeatherWidget: React.FC<WidgetProps> = ({
  scope: _scope,
  projectId: _projectId,
  size,
  config,
}) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mock weather data - in production, this would call a weather API
  useEffect(() => {
    const fetchWeather = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));

        // Mock data - replace with actual weather API call
        const mockWeather: WeatherData = {
          location: config?.location || 'Project Site',
          temperature: 72,
          feelsLike: 70,
          condition: 'sunny',
          description: 'Clear skies',
          humidity: 45,
          windSpeed: 8,
          forecast: [
            { day: 'Mon', high: 75, low: 58, condition: 'sunny' },
            { day: 'Tue', high: 73, low: 56, condition: 'cloudy' },
            { day: 'Wed', high: 68, low: 52, condition: 'rainy' },
            { day: 'Thu', high: 70, low: 54, condition: 'cloudy' },
            { day: 'Fri', high: 74, low: 57, condition: 'sunny' },
          ],
        };

        setWeather(mockWeather);
      } catch {
        setError('Failed to load weather data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeather();

    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [config?.location]);

  const getWeatherIcon = (condition: string, size: 'sm' | 'lg' = 'sm') => {
    const iconClass = size === 'lg' ? 'h-10 w-10' : 'h-5 w-5';
    switch (condition) {
      case 'sunny':
        return <Sun className={`${iconClass} text-yellow-500`} />;
      case 'cloudy':
        return <Cloud className={`${iconClass} text-gray-400`} />;
      case 'rainy':
        return <CloudRain className={`${iconClass} text-blue-500`} />;
      case 'snowy':
        return <CloudSnow className={`${iconClass} text-blue-200`} />;
      case 'windy':
        return <Wind className={`${iconClass} text-gray-500`} />;
      default:
        return <Sun className={`${iconClass} text-yellow-500`} />;
    }
  };

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={3} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">{error}</p>
          <button
            onClick={() => setIsLoading(true)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center justify-center mx-auto"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!weather) {
    return (
      <WidgetEmptyState
        title="No Weather Data"
        description="Configure location to see weather"
        icon={Cloud}
      />
    );
  }

  const isCompact = size === '1x1';

  return (
    <div className="space-y-4">
      {/* Current Weather */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {getWeatherIcon(weather.condition, 'lg')}
          <div>
            <p className="text-3xl font-bold text-gray-900">{weather.temperature}°F</p>
            <p className="text-xs text-gray-500">{weather.description}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center text-xs text-gray-500 justify-end">
            <MapPin className="h-3 w-3 mr-1" />
            <span className="truncate max-w-[80px]">{weather.location}</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Feels like {weather.feelsLike}°</p>
        </div>
      </div>

      {/* Weather Details */}
      {!isCompact && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 rounded-lg p-2 flex items-center space-x-2">
            <Droplets className="h-4 w-4 text-blue-500" />
            <div>
              <p className="text-xs text-gray-500">Humidity</p>
              <p className="text-sm font-medium text-gray-900">{weather.humidity}%</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 flex items-center space-x-2">
            <Wind className="h-4 w-4 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Wind</p>
              <p className="text-sm font-medium text-gray-900">{weather.windSpeed} mph</p>
            </div>
          </div>
        </div>
      )}

      {/* Forecast (for larger sizes) */}
      {size === '2x2' && weather.forecast && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">5-Day Forecast</p>
          <div className="flex justify-between">
            {weather.forecast.map((day, idx) => (
              <div key={idx} className="text-center">
                <p className="text-xs text-gray-500 mb-1">{day.day}</p>
                {getWeatherIcon(day.condition, 'sm')}
                <div className="mt-1">
                  <p className="text-xs font-medium text-gray-900">{day.high}°</p>
                  <p className="text-xs text-gray-400">{day.low}°</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Work Impact Notice */}
      {weather.condition === 'rainy' || weather.condition === 'snowy' ? (
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-2">
          <p className="text-xs text-yellow-700">⚠️ Weather may impact outdoor work today</p>
        </div>
      ) : null}
    </div>
  );
};

export default WeatherWidget;
